import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import { Box, ButtonBase, Typography } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { PatientFormI18nContext, usePatientFormI18n } from '../utils/patientFormI18n'
import {
  CONSENT_MODE_CLINICAL,
  CONSENT_MODE_NONE,
  CONSENT_MODE_SUPPORT,
  getClinicalConsent,
  isConsentPending,
} from '../utils/patientConsentUtils'
import { PATIENT_FORM_TOKENS as T, PatientFieldHint } from './PatientFormPrimitives'

function formatTakenOn(value) {
  if (!value) return ''
  const date = new Date(`${value}`.replace(' ', 'T') + (`${value}`.endsWith('Z') ? '' : 'Z'))
  if (Number.isNaN(date.getTime())) return `${value}`
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

/**
 * The consent block at the end of the form.
 *
 * - clinical mode: the statement + a large checkbox that must be ticked before "Add Patient".
 *   When the host bumps `nudgeToken` (user tried to submit without ticking) the block scrolls
 *   into view, pulses, and tells the user exactly what to do.
 * - support mode: an explanatory banner — support cannot take clinical consent, so the record
 *   is saved as "pending" for the clinic to complete.
 * - existing patients: shows who recorded consent and when, or that it is still pending
 *   (with the checkbox available to record it now).
 */
export default function PatientConsentSection({
  mode = CONSENT_MODE_NONE,
  patientDetails,
  editMode = true,
  onChange,
  nudgeToken = 0,
  requiredEmpty = false,
}) {
  const { copy } = usePatientFormI18n()
  const rootRef = useRef(null)
  const checkboxRef = useRef(null)
  const [nudged, setNudged] = useState(false)

  useEffect(() => {
    if (!nudgeToken) return undefined
    setNudged(true)
    rootRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
    const focusTimer = setTimeout(() => checkboxRef.current?.focus?.(), 350)
    const clearTimer = setTimeout(() => setNudged(false), 3200)
    return () => {
      clearTimeout(focusTimer)
      clearTimeout(clearTimer)
    }
  }, [nudgeToken])

  const existing = getClinicalConsent(patientDetails)
  const recorded = existing && existing.accepted && existing.takenOn
  const pending = isConsentPending(patientDetails)
  const draftAccepted = Boolean(patientDetails?.consentDraft?.accepted)

  if (mode === CONSENT_MODE_NONE && !existing) {
    return null
  }

  // Existing, recorded consent: show provenance only.
  if (recorded && !draftAccepted) {
    return (
      <Box
        ref={rootRef}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          padding: '10px 12px',
          borderRadius: '10px',
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
        }}
      >
        <CheckCircleIcon sx={{ fontSize: '20px', color: T.success, marginTop: '1px' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography component="div" sx={{ fontSize: '13px', fontWeight: 700, color: '#166534' }}>
            {copy.consentRecordedTitle}
          </Typography>
          <PatientFieldHint sx={{ color: '#166534', marginTop: '2px' }}>
            {copy.consentRecordedBy(existing.takenByName || existing.takenByUserId || '', formatTakenOn(existing.takenOn), existing.takenVia || '')}
          </PatientFieldHint>
        </Box>
      </Box>
    )
  }

  // Support portal: cannot take clinical consent.
  if (mode === CONSENT_MODE_SUPPORT) {
    return (
      <Box
        ref={rootRef}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          padding: '10px 12px',
          borderRadius: '10px',
          background: '#fffbeb',
          border: '1px solid #fde68a',
        }}
      >
        <HourglassBottomIcon sx={{ fontSize: '20px', color: '#b45309', marginTop: '1px' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography component="div" sx={{ fontSize: '13px', fontWeight: 700, color: '#92400e' }}>
            {copy.consentSupportPendingTitle}
          </Typography>
          <PatientFieldHint sx={{ color: '#92400e', marginTop: '2px' }}>
            {copy.consentSupportPendingBody}
          </PatientFieldHint>
        </Box>
      </Box>
    )
  }

  const showCheckbox = editMode && (mode === CONSENT_MODE_CLINICAL || pending)
  if (!showCheckbox) {
    if (pending) {
      return (
        <Box ref={rootRef} sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '10px', background: '#fffbeb', border: '1px solid #fde68a' }}>
          <HourglassBottomIcon sx={{ fontSize: '18px', color: '#b45309' }} />
          <Typography component="span" sx={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>
            {copy.consentPendingChip}
          </Typography>
        </Box>
      )
    }
    return null
  }

  const highlight = nudged || requiredEmpty
  const isRequired = mode === CONSENT_MODE_CLINICAL

  return (
    <Box
      ref={rootRef}
      data-testid="patient-consent-section"
      data-nudged={nudged ? 'true' : undefined}
      sx={{
        position: 'relative',
        padding: '12px',
        borderRadius: '12px',
        background: draftAccepted ? '#f0fdf4' : highlight ? '#fff5f5' : T.surfaceSoft,
        border: `1px solid ${draftAccepted ? '#bbf7d0' : highlight ? '#fca5a5' : '#e2e8f0'}`,
        boxShadow: nudged ? '0 0 0 4px rgba(185, 28, 28, 0.18)' : 'none',
        transition: 'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        '@keyframes patientConsentPulse': {
          '0%': { boxShadow: '0 0 0 0 rgba(185, 28, 28, 0.45)' },
          '70%': { boxShadow: '0 0 0 10px rgba(185, 28, 28, 0)' },
          '100%': { boxShadow: '0 0 0 0 rgba(185, 28, 28, 0)' },
        },
        animation: nudged ? 'patientConsentPulse 1.1s ease-out 2' : 'none',
      }}
    >
      {pending && !draftAccepted && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
          <HourglassBottomIcon sx={{ fontSize: '16px', color: '#b45309' }} />
          <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 700, color: '#92400e' }}>
            {copy.consentPendingChip}
          </Typography>
        </Box>
      )}

      <Box component="label" htmlFor="patient-consent" sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start', cursor: 'pointer' }}>
        <Box
          component="input"
          ref={checkboxRef}
          id="patient-consent"
          type="checkbox"
          checked={draftAccepted}
          onChange={(event) => onChange?.(event.target.checked)}
          sx={{
            width: { xs: '26px', sm: '22px' },
            height: { xs: '26px', sm: '22px' },
            marginTop: '1px',
            flexShrink: 0,
            accentColor: draftAccepted ? T.success : T.accent,
            cursor: 'pointer',
            outline: nudged ? `3px solid ${T.error}` : 'none',
            outlineOffset: '2px',
            borderRadius: '4px',
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography component="div" sx={{ fontSize: '13.5px', fontWeight: 700, color: draftAccepted ? '#166534' : highlight ? T.error : '#1e293b' }}>
            {copy.consentTitle}
            {isRequired && !draftAccepted && (
              <Box component="span" aria-hidden sx={{ color: T.error, marginLeft: '2px' }}>*</Box>
            )}
          </Typography>
          <Typography component="div" sx={{ fontSize: '12.5px', lineHeight: 1.5, color: '#334155', marginTop: '4px' }}>
            {copy.consentStatement}
          </Typography>
        </Box>
      </Box>

      {nudged && !draftAccepted && (
        <Box
          role="alert"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '10px',
            padding: '8px 10px',
            borderRadius: '8px',
            background: '#fee2e2',
            color: '#991b1b',
            fontSize: '13px',
            fontWeight: 700,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: '18px' }} />
          {copy.consentNudgeMessage}
        </Box>
      )}
    </Box>
  )
}


/**
 * Compact consent row for a pinned dialog footer: checkbox + short statement, with the full
 * statement one tap away. Same state, nudge and modes as the full section; hosts that render
 * this pass `consentPlacement="footer"` to the form so the block is not shown twice.
 * `i18n` is the object from buildPatientFormI18n (hosts sit outside the form's provider).
 */
export function PatientConsentStrip({ i18n, ...props }) {
  const strip = <PatientConsentStripInner {...props} />
  return i18n ? <PatientFormI18nContext.Provider value={i18n}>{strip}</PatientFormI18nContext.Provider> : strip
}

function PatientConsentStripInner({
  mode = CONSENT_MODE_NONE,
  patientDetails,
  editMode = true,
  onChange,
  nudgeToken = 0,
}) {
  const { copy } = usePatientFormI18n()
  const checkboxRef = useRef(null)
  const rootRef = useRef(null)
  const [nudged, setNudged] = useState(false)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (!nudgeToken) return undefined
    setNudged(true)
    setExpanded(true)
    rootRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
    const focusTimer = setTimeout(() => checkboxRef.current?.focus?.(), 250)
    const clearTimer = setTimeout(() => setNudged(false), 3200)
    return () => {
      clearTimeout(focusTimer)
      clearTimeout(clearTimer)
    }
  }, [nudgeToken])

  if (mode === CONSENT_MODE_SUPPORT) {
    return (
      <Box
        ref={rootRef}
        sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a' }}
      >
        <HourglassBottomIcon sx={{ fontSize: '18px', color: '#b45309', flexShrink: 0 }} />
        <Typography component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: '#92400e', lineHeight: 1.4 }}>
          {copy.consentSupportPendingTitle}
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, fontWeight: 500 }}>
            {' — '}
            {copy.consentSupportPendingBody}
          </Box>
        </Typography>
      </Box>
    )
  }
  if (mode !== CONSENT_MODE_CLINICAL || !editMode) {
    return null
  }

  const accepted = Boolean(patientDetails?.consentDraft?.accepted)
  return (
    <Box
      ref={rootRef}
      data-testid="patient-consent-strip"
      data-nudged={nudged ? 'true' : undefined}
      sx={{
        padding: '8px 10px',
        borderRadius: '10px',
        background: accepted ? '#f0fdf4' : nudged ? '#fff5f5' : T.surfaceSoft,
        border: `1px solid ${accepted ? '#bbf7d0' : nudged ? '#fca5a5' : '#e2e8f0'}`,
        boxShadow: nudged ? '0 0 0 4px rgba(185, 28, 28, 0.18)' : 'none',
        transition: 'background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
        '@keyframes patientConsentStripPulse': {
          '0%': { boxShadow: '0 0 0 0 rgba(185, 28, 28, 0.45)' },
          '70%': { boxShadow: '0 0 0 10px rgba(185, 28, 28, 0)' },
          '100%': { boxShadow: '0 0 0 0 rgba(185, 28, 28, 0)' },
        },
        animation: nudged ? 'patientConsentStripPulse 1.1s ease-out 2' : 'none',
      }}
    >
      <Box component="label" htmlFor="patient-consent" sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer' }}>
        <Box
          component="input"
          ref={checkboxRef}
          id="patient-consent"
          type="checkbox"
          checked={accepted}
          onChange={(event) => onChange?.(event.target.checked)}
          sx={{
            width: { xs: '26px', sm: '22px' },
            height: { xs: '26px', sm: '22px' },
            flexShrink: 0,
            accentColor: accepted ? T.success : T.accent,
            cursor: 'pointer',
            outline: nudged ? `3px solid ${T.error}` : 'none',
            outlineOffset: '2px',
            borderRadius: '4px',
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography component="div" sx={{ fontSize: '12.5px', lineHeight: 1.45, color: accepted ? '#166534' : nudged ? T.error : '#1e293b' }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {copy.consentTitle}
              {!accepted && (
                <Box component="span" aria-hidden sx={{ color: T.error, marginLeft: '2px' }}>*</Box>
              )}
            </Box>
            {' — '}
            {expanded ? copy.consentStatement : copy.consentStripShort}{' '}
            <ButtonBase
              onClick={(event) => {
                event.preventDefault()
                setExpanded((value) => !value)
              }}
              sx={{ fontSize: '12px', fontWeight: 700, color: T.accent, verticalAlign: 'baseline', padding: '0 2px', minHeight: '20px' }}
            >
              {expanded ? copy.consentStripReadLess : copy.consentStripReadMore}
            </ButtonBase>
          </Typography>
        </Box>
      </Box>
      {nudged && !accepted && (
        <Box role="alert" sx={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', color: '#991b1b', fontSize: '12.5px', fontWeight: 700 }}>
          <ErrorOutlineIcon sx={{ fontSize: '16px' }} />
          {copy.consentNudgeMessage}
        </Box>
      )}
    </Box>
  )
}
